import { useMemo, useState } from 'react'
import {
  Card,
  Table,
  formatCurrency,
  formatDate,
  formatNumber,
  type TableColumn,
} from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import { CHANNEL_LABELS, PRODUCT_TYPE_LABELS } from './labels'
import { JourneyDrawer } from './JourneyDrawer'
import type { AttributionRow, PaymentAttributionData } from './types'
import styles from '../PaymentAttribution.module.css'

// ── 결제건별 정렬 ──
type SortKey = 'paidAt' | 'netAmount' | 'signupAt' | 'daysToPay'

function sortValue(r: AttributionRow, key: string): string | number | null {
  switch (key) {
    case 'paidAt': return r.paidAt
    case 'netAmount': return r.netAmount
    case 'signupAt': return r.signupAt
    case 'daysToPay': return r.daysToPay
    default: return null
  }
}

interface JourneyTabProps {
  data: PaymentAttributionData | null
  error: string | null
}

export function JourneyTab({ data, error }: JourneyTabProps) {
  const [sortBy, setSortBy] = useState<SortKey>('paidAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null)

  const sortedRows = useMemo(() => {
    const rows = [...(data?.rows ?? [])]
    const dir = sortOrder === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      const av = sortValue(a, sortBy)
      const bv = sortValue(b, sortBy)
      if (av == null && bv == null) return 0
      if (av == null) return 1 // null은 항상 뒤로
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
    return rows
  }, [data, sortBy, sortOrder])

  const columns: TableColumn<AttributionRow>[] = [
    { key: 'paidAt', label: '결제일시', render: (r) => formatDate(r.paidAt, 'M.d HH:mm'), width: '90px', sortable: true },
    { key: 'channel', label: '채널', render: (r) => CHANNEL_LABELS[r.channel] ?? r.channel, width: '110px' },
    { key: 'type', label: '상품', render: (r) => <StatusBadge status={r.type} label={PRODUCT_TYPE_LABELS[r.type] ?? r.type} />, width: '130px' },
    { key: 'netAmount', label: '순매출', render: (r) => formatCurrency(r.netAmount), align: 'right' as const, width: '100px', sortable: true },
    { key: 'user', label: '유저', render: (r) => r.userName ?? (r.userId ? r.userId.slice(0, 8) : '— 미활성화') },
    { key: 'signupAt', label: '가입일', render: (r) => (r.signupAt ? formatDate(r.signupAt, 'yy.M.d') : '-'), width: '80px', sortable: true },
    { key: 'daysToPay', label: '가입→결제', render: (r) => (r.daysToPay != null ? `${r.daysToPay}일` : '-'), align: 'right' as const, width: '90px', sortable: true },
    {
      key: 'utm',
      label: '유입 (first-touch)',
      render: (r) =>
        r.utmSource ? (
          <span className={styles.utmCell}>
            {r.utmSource}
            {r.utmMedium ? ` / ${r.utmMedium}` : ''}
            {r.utmContent ? ` / ${r.utmContent}` : ''}
          </span>
        ) : r.userId ? (
          <span className={styles.utmCell}>(direct)</span>
        ) : (
          <span className={styles.utmCell}>-</span>
        ),
    },
  ]

  return (
    <>
      <Card>
        <p className={styles.cardTitle}>결제 건별 역추적 ({formatNumber(data?.rows.length ?? 0)}건)</p>
        <p className={styles.cardCaption}>행을 클릭하면 결제 전후 여정 상세를 확인할 수 있습니다.</p>
        <Table<AttributionRow>
          columns={columns}
          data={sortedRows}
          rowKey={(r) => r.paymentId}
          isLoading={!data && !error}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={(key, order) => { setSortBy(key as SortKey); setSortOrder(order) }}
          onRowClick={(row) => setSelectedPaymentId(row.paymentId)}
        />
      </Card>

      <JourneyDrawer
        open={selectedPaymentId != null}
        paymentId={selectedPaymentId}
        onClose={() => setSelectedPaymentId(null)}
      />
    </>
  )
}
